/**
 * Verknüpft die we-abonnement UI mit dem Recharge-Widget (technisches Backend).
 * Das Recharge-Widget bleibt im Produktformular, wird per CSS ausgeblendet.
 */
(function () {
  const RC = {
    wrapper: '.rc-container-wrapper',
    container: '.rc-container',
    radioGroup: '[data-radio-group-options]',
    radioInput: '.rc-radio__input',
    sellingPlansSelect: '.rc-selling-plans__dropdown, .rc-selling-plans-dropdown__select, select[name="selling_plan"]',
    optionPrice: '.rc-option__price',
    optionDiscount: '.rc-option__discount',
  };

  function getProductForm() {
    return (
      document.querySelector('product-form form[data-type="add-to-cart-form"]') ||
      document.querySelector('.product-form form')
    );
  }

  function clickInput(input) {
    if (!input) return;
    input.click();
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function waitForRecharge(form, timeoutMs) {
    return new Promise((resolve, reject) => {
      const existing = form?.querySelector(RC.container);
      if (existing) return resolve(existing);

      const deadline = Date.now() + timeoutMs;
      const observer = new MutationObserver(() => {
        const container = form?.querySelector(RC.container);
        if (container) {
          observer.disconnect();
          resolve(container);
        } else if (Date.now() > deadline) {
          observer.disconnect();
          reject(new Error('Recharge widget not found'));
        }
      });

      if (!form) {
        reject(new Error('Product form not found'));
        return;
      }

      observer.observe(form, { childList: true, subtree: true });

      const poll = () => {
        const container = form.querySelector(RC.container);
        if (container) {
          observer.disconnect();
          resolve(container);
        } else if (Date.now() > deadline) {
          observer.disconnect();
          reject(new Error('Recharge widget not found'));
        } else {
          requestAnimationFrame(poll);
        }
      };
      poll();
    });
  }

  class WeAbonnementRechargeBridge {
    constructor() {
      this.form = getProductForm();
      this.container = null;
      this.ready = false;
      this.isSyncing = false;
      this._onRcMutate = this._onRcMutate.bind(this);
      this._observer = null;
    }

    async init() {
      if (!this.form) return false;
      try {
        this.container = await waitForRecharge(this.form, 20000);
      } catch {
        return false;
      }

      this.ready = true;
      this._observeRecharge();
      return true;
    }

    _observeRecharge() {
      if (this._observer) this._observer.disconnect();
      this._observer = new MutationObserver(this._onRcMutate);
      const target = this.form.querySelector(RC.wrapper) || this.container;
      if (target) {
        this._observer.observe(target, { childList: true, subtree: true, attributes: true });
      }
    }

    _onRcMutate() {
      if (this.isSyncing) return;
      document.dispatchEvent(new CustomEvent('we-abonnement:recharge-updated'));
    }

    _getRadioInputs() {
      const group = this.container?.querySelector(RC.radioGroup);
      if (!group) {
        return this.container
          ? Array.from(this.container.querySelectorAll(RC.radioInput))
          : [];
      }
      return Array.from(group.querySelectorAll(RC.radioInput));
    }

    getPurchaseOptions() {
      const inputs = this._getRadioInputs();
      if (!inputs.length) return { onetime: null, subscription: null };

      const onetime =
        inputs.find(
          (input) =>
            input.value === 'onetime' ||
            input.getAttribute('data-option') === 'onetime' ||
            input.closest('.rc-widget__option--onetime, .rc-option--onetime')
        ) || inputs[0];

      const subscription =
        inputs.find((input) => input !== onetime && input.checked === false) ||
        inputs.find((input) => input !== onetime) ||
        inputs[1] ||
        null;

      return { onetime, subscription };
    }

    selectOneTime() {
      if (!this.ready) return;
      this.isSyncing = true;
      const { onetime } = this.getPurchaseOptions();
      clickInput(onetime);
      queueMicrotask(() => {
        this.isSyncing = false;
      });
    }

    selectSubscription() {
      if (!this.ready) return;
      this.isSyncing = true;
      const { subscription } = this.getPurchaseOptions();
      if (subscription && !subscription.checked) {
        clickInput(subscription);
      }
      queueMicrotask(() => {
        this.isSyncing = false;
      });
    }

    selectSellingPlan(sellingPlanId) {
      if (!this.ready || !sellingPlanId) return;
      this.isSyncing = true;

      const { subscription } = this.getPurchaseOptions();
      if (subscription && !subscription.checked) {
        clickInput(subscription);
      }

      const select = this.container.querySelector(RC.sellingPlansSelect);
      if (select) {
        const id = String(sellingPlanId);
        const option = Array.from(select.options).find(
          (opt) => opt.value === id || opt.dataset.sellingPlanId === id
        );
        if (option && select.value !== option.value) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          select.dispatchEvent(new Event('input', { bubbles: true }));
        }
      } else {
        const planInputs = Array.from(
          this.container.querySelectorAll(
            '.rc-selling-plans .rc-radio__input, .rc-plans-radio-button input'
          )
        );
        const match = planInputs.find(
          (input) =>
            input.value === String(sellingPlanId) ||
            input.dataset.sellingPlanId === String(sellingPlanId)
        );
        clickInput(match);
      }

      queueMicrotask(() => {
        this.isSyncing = false;
      });
    }

    readSubscriptionUi() {
      if (!this.ready) return null;
      const select = this.container.querySelector(RC.sellingPlansSelect);
      if (select && select.selectedOptions[0]) {
        const opt = select.selectedOptions[0];
        return {
          name: opt.textContent.trim(),
          priceText: '',
        };
      }
      const activePlan = this.container.querySelector(
        '.rc-selling-plans .rc-radio__input:checked, .rc-plans-radio-button input:checked'
      );
      if (activePlan) {
        const label = activePlan.closest('.rc-radio')?.querySelector('.rc-radio__label');
        return { name: label?.textContent?.trim() || '', priceText: '' };
      }
      return null;
    }

    /** Höchster Rabatt in % aus Recharge-Optionen (Einmalpreis vs. Abo-Preis). */
    getMaxDiscountPercent() {
      if (!this.ready) return 0;

      const { onetime, subscription } = this.getPurchaseOptions();
      const subRadio = subscription?.closest('.rc-radio');
      if (!subRadio) return 0;

      const discountText = subRadio.querySelector(RC.optionDiscount)?.textContent || '';
      const discountMatch = discountText.match(/(\d+)\s*%/);
      if (discountMatch) return parseInt(discountMatch[1], 10);

      const parsePrice = (text) => {
        if (!text) return NaN;
        const normalized = text.replace(/\s/g, '').replace(/[^\d,.-]/g, '');
        const lastComma = normalized.lastIndexOf(',');
        const lastDot = normalized.lastIndexOf('.');
        let numeric = normalized;
        if (lastComma > lastDot) {
          numeric = normalized.replace(/\./g, '').replace(',', '.');
        } else {
          numeric = normalized.replace(/,/g, '');
        }
        return parseFloat(numeric);
      };

      const onetimePrice = parsePrice(
        onetime?.closest('.rc-radio')?.querySelector(RC.optionPrice)?.textContent
      );
      const subscriptionPrice = parsePrice(subRadio.querySelector(RC.optionPrice)?.textContent);

      if (
        !Number.isFinite(onetimePrice) ||
        !Number.isFinite(subscriptionPrice) ||
        onetimePrice <= 0 ||
        subscriptionPrice >= onetimePrice
      ) {
        return 0;
      }

      return Math.round(((onetimePrice - subscriptionPrice) / onetimePrice) * 100);
    }
  }

  window.WeAbonnementRechargeBridge = WeAbonnementRechargeBridge;

  document.addEventListener('DOMContentLoaded', async () => {
    const root = document.querySelector('.selling_plan_app_container');
    if (!root) return;

    const bridge = new WeAbonnementRechargeBridge();
    const ok = await bridge.init();
    if (ok) {
      window.weAbonnementRechargeBridge = bridge;
      document.dispatchEvent(
        new CustomEvent('we-abonnement:recharge-ready', { detail: { bridge } })
      );
    }
  });
})();
